<script setup lang="ts">
import type { SwiperOptions } from 'swiper/types';
import { Swiper } from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css/bundle';
import {
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { type PhotoSizeCollection } from '@clients/HotelApi';
import type { ButtonSize, SizeConditional } from '#imports';

const {
    images = [],
    noSlider,
    noBorderRadius,
    showButtonOnHover,
    hideButtons,
    pagination,
    mobileImageFit = 'cover',
    desktopImageFit = 'cover',
    mobileSlider,
    buttonSize = 'medium',
} = defineProps<IProps>();

Swiper.use([Pagination, Navigation]);

interface IProps {
    images?: PhotoSizeCollection[];
    noSlider?: boolean;
    noBorderRadius?: boolean;
    showButtonOnHover?: boolean;
    hideButtons?: boolean;
    pagination?: boolean;
    mobileImageFit?: 'unset' | 'contain' | 'cover' | 'fill' | 'scale-down';
    desktopImageFit?: 'unset' | 'contain' | 'cover' | 'fill' | 'scale-down';
    mobileSlider?: boolean;
    buttonSize?: SizeConditional<ButtonSize>;
    preloadFirstImage?: boolean;
}
const wrapper = ref<HTMLDivElement>();
const swiperContainer = ref<HTMLDivElement>();
const sliderContainer = ref<HTMLDivElement>();

const swiperActiveIndex = ref(0);
const swiper = ref<Swiper>();
const slider = ref<Swiper>();
const { isMobile } = useSize();
const mobileSliderEnabled = computed(() => isMobile.value && mobileSlider);
const paginationEnabled = computed(
    () => pagination && !mobileSliderEnabled.value
);
const navButtonsEnabled = computed(
    () => !hideButtons && !mobileSliderEnabled.value
);
const mountSwiper = () =>
    nextTick(() => {
        const options: SwiperOptions = {
            spaceBetween: 0,
            on: {
                activeIndexChange: swiperChange,
            },
        };
        if (paginationEnabled.value) {
            options.pagination = {
                el: '.swiper-pagination',
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 3,
            };
        }
        if (navButtonsEnabled.value) {
            options.navigation = {
                nextEl: '.arrow-button.left',
                prevEl: '.arrow-button.right',
            };
        }
        if (mobileSliderEnabled.value) {
            options.freeMode = true;
            options.spaceBetween = 4;
            options.touchRatio = 0.2;
            options.centeredSlides = false;
            options.slidesOffsetAfter = 0;
            options.slidesOffsetBefore = 5;
            options.slidesPerView = 'auto';
        }
        if (swiperContainer.value) {
            swiper.value = new Swiper(swiperContainer.value, options);
        }
        if (!noSlider && sliderContainer.value) {
            slider.value = new Swiper(sliderContainer.value, {
                spaceBetween: 8,
                slidesPerView: 5,
                touchRatio: 0.2,
                slideToClickedSlide: true,
                freeMode: true,
                on: {
                    click: swipe => {
                        swiper.value?.slideTo(swipe.clickedIndex);
                    },
                },
            });
        }
    });
const swiperChange = () => {
    if (swiper.value) {
        swiperActiveIndex.value = swiper.value.activeIndex;
        slider.value?.slideToLoop(swiper.value.activeIndex);
    }
};
const destroySwiper = () => {
    swiper.value?.destroy();
    slider.value?.destroy();
    swiper.value = undefined;
    slider.value = undefined;
};
watch(
    () => [images, mobileSliderEnabled.value],
    () => {
        destroySwiper();
        mountSwiper();
    }
);
onMounted(() => {
    mountSwiper();
});
onBeforeUnmount(() => destroySwiper());
</script>

<template>
    <div ref="wrapper" class="image-carousel-container">
        <div
            ref="swiperContainer"
            class="swiper-container"
            :class="{
                'no-slider': noSlider,
                'no-border': noBorderRadius,
                'hover-show-button': showButtonOnHover,
                'mobile-slider': mobileSliderEnabled,
            }"
        >
            <mr-button
                v-if="navButtonsEnabled"
                v-show="swiperActiveIndex !== 0"
                class="arrow-button right"
                :size="buttonSize"
                :icon-button="{
                    icon: faChevronRight,
                    color: 'white',
                }"
                @click.stop.prevent
            />
            <div class="swiper-wrapper">
                <div
                    v-for="(image, index) in images"
                    :key="image.blurhash || image.large || `image-${index}`"
                    class="swiper-slide"
                >
                    <mr-blur-hash-image
                        class="swiper-image"
                        :hash="image.blurhash ?? undefined"
                        :src="image.large || undefined"
                        :image-fit="isMobile ? mobileImageFit : desktopImageFit"
                        :alt="image.alt ?? undefined"
                        :preload="preloadFirstImage && index === 0"
                    />
                </div>
            </div>
            <mr-button
                v-if="navButtonsEnabled"
                v-show="swiperActiveIndex !== images.length - 1"
                class="arrow-button left"
                :size="buttonSize"
                :icon-button="{
                    icon: faChevronLeft,
                    color: 'white',
                }"
                @click.stop.prevent
            />
            <div
                v-show="paginationEnabled"
                class="swiper-pagination"
                @click.stop.prevent
            />
        </div>
        <div v-show="!noSlider" ref="sliderContainer" class="slider-container">
            <div class="swiper-wrapper">
                <div
                    v-for="(image, index) in images"
                    :key="index"
                    class="swiper-slide"
                >
                    <mr-blur-hash-image
                        :hash="image.blurhash ?? undefined"
                        :src="image.small || undefined"
                        :border-radius="8"
                        :alt="image.alt ?? undefined"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.image-carousel-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.swiper-container {
    height: calc(100% - 97px);
    border-radius: 16px;
    &.no-slider {
        height: 100%;
    }
    &.no-border {
        border-radius: 0;
        .swiper-image :deep() {
            img,
            canvas {
                border-radius: 0 !important;
            }
        }
    }
    &.hover-show-button {
        &:hover {
            .arrow-button {
                opacity: 1;
            }
        }
        .arrow-button {
            transition: all 200ms;
            opacity: 0;
        }
    }
    .swiper-slide {
        width: 100%;
        height: 100%;
    }

    .arrow-button {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;

        &.right {
            right: 12px;
        }
        &.left {
            left: 12px;
        }
    }
}

.slider-container {
    width: 100%;
    height: 88px;
    margin-top: 12px;
    .swiper-slide {
        width: 129px;
        height: 85px;
    }

    :deep() {
        img {
            opacity: 1 !important;
        }
    }
}

.swiper-pagination :deep() {
    .swiper-pagination-bullet {
        width: 8px;
        height: 8px;
        background: black;
        opacity: 0.2;
        &.swiper-pagination-bullet-active {
            background: white;
            opacity: 1;
        }
    }
}

@media (max-width: $app-mobile-max-width) {
    .image-carousel-container {
        .arrow-button {
            &.right {
                right: 8px;
            }
            &.left {
                left: 8px;
            }
        }
    }
    .swiper-container {
        height: 100%;

        &.mobile-slider {
            @include no-scrollbar;
            overflow-x: scroll;
            .swiper-wrapper {
                width: fit-content;
            }
            .swiper-slide {
                width: 250px !important;
                height: 166px;
                margin-left: 4px;
            }
            .swiper-image :deep() {
                img,
                canvas {
                    border-radius: 8px !important;
                }
            }
        }

        .swiper-image :deep() {
            img,
            canvas {
                border-radius: 0 !important;
            }
        }
    }
}
</style>
